import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { RecruiterAnalytics } from "@/components/RecruiterAnalytics";
import { Plus } from "lucide-react";

export const RecruiterDashboard = () => {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [counts, setCounts] = useState({});

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/jobs/mine");
      setJobs(data);
      const c = {};
      await Promise.all(data.map(async (j) => {
        try {
          const r = await api.get(`/applications/job/${j.job_id}`);
          c[j.job_id] = r.data.length;
        } catch { c[j.job_id] = 0; }
      }));
      setCounts(c);
    })();
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <div className="mono-label">recruiter</div>
      <div className="flex items-end justify-between mt-2 flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Welcome, {user?.name?.split(" ")[0]}.</h1>
          <p className="text-slate-400 mt-2">Every applicant. Ranked. Instantly.</p>
        </div>
        <Link to="/recruiter/post">
          <Button data-testid="recruiter-post-btn" className="bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-full px-6">
            <Plus className="w-4 h-4 mr-2" /> Post a job
          </Button>
        </Link>
      </div>

      <RecruiterAnalytics />

      <div className="mt-12">
        <h2 className="font-display text-2xl font-semibold mb-4">Your job posts</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {jobs.map((j) => (
            <Link
              key={j.job_id}
              to={`/recruiter/jobs/${j.job_id}`}
              data-testid={`recruiter-job-${j.job_id}`}
              className="border border-white/10 bg-[#111827] rounded-xl p-5 hover:border-white/20 hover:-translate-y-0.5 transition-transform"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="font-display text-lg font-medium">{j.title}</div>
                  <div className="text-sm text-slate-400 mt-1">{j.company} · {j.location}</div>
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {(j.required_skills || []).slice(0, 4).map((s) => (
                      <span key={s} className="text-xs px-2 py-0.5 rounded border border-white/10 text-slate-300">{s}</span>
                    ))}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-display text-2xl">{counts[j.job_id] ?? "—"}</div>
                  <div className="mono-label mt-1">applicants</div>
                </div>
              </div>
            </Link>
          ))}
          {jobs.length === 0 && (
            <div className="col-span-2 border border-dashed border-white/10 rounded-xl p-10 text-center text-slate-500">
              No job posts yet. <Link to="/recruiter/post" className="text-[#06B6D4] hover:underline">Post your first job</Link>.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
